/**
 * @file ThemeSwitcher.js
 * @description Selector rápido de paleta y de modo claro/oscuro para la navbar.
 *
 * ⚠ TEMPORAL. Está mientras el equipo y los clientes prueban las tres paletas.
 * Cuando se elija una, este componente y `src/theme/` desaparecen.
 *
 * Deliberadamente vive en la navbar y no escondido en Configuración: la gracia
 * es que se pueda cambiar sobre la pantalla que se está mirando, sin perder el
 * contexto, para poder comparar de verdad.
 */
import React from 'react';
import { NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { PALETTES, MODES } from '../theme/palettes';

/** Tira de muestras de una paleta. Los colores salen de los tokens, no de hex. */
export const PaletteSwatches = ({ paletteId, swatches, size = 14 }) => (
    <span
        className="d-inline-flex align-items-center gap-1"
        // data-ev-palette local: obliga a que las muestras pinten los colores de
        // ESA paleta, no los de la que está activa ahora mismo.
        data-ev-palette={paletteId}
        aria-hidden="true"
    >
        {swatches.map((token) => (
            <span
                key={token}
                style={{
                    width: size,
                    height: size,
                    borderRadius: 3,
                    backgroundColor: `var(${token})`,
                    border: '1px solid var(--ev-border-default)',
                    display: 'inline-block',
                    flex: 'none',
                }}
            />
        ))}
    </span>
);

const ThemeSwitcher = () => {
    const { t } = useTranslation();
    const { palette, mode, isDark, canChoose, setPalette, setMode } = useTheme();

    // El superadmin puede cerrar la elección desde el backoffice. Cuando lo hace,
    // el control no se deshabilita: se va. Un selector gris que no responde
    // genera más preguntas que su ausencia.
    if (!canChoose) return null;

    const activeIcon = isDark ? 'bi-moon-stars-fill' : 'bi-sun-fill';

    return (
        <NavDropdown
            title={
                <>
                    <i className={`bi ${activeIcon} me-2`} />
                    <span className="d-lg-none">{t('appearance.navTitle')}</span>
                    <span className="d-none d-lg-inline text-uppercase">{palette}</span>
                </>
            }
            id="theme-nav-dropdown"
            align="end"
            data-testid="theme-switcher"
        >
            <NavDropdown.Header>{t('appearance.paletteLabel')}</NavDropdown.Header>
            {PALETTES.map((p) => (
                <NavDropdown.Item
                    key={p.id}
                    onClick={() => setPalette(p.id)}
                    active={palette === p.id}
                    data-testid={`theme-palette-${p.id}`}
                >
                    <span className="d-flex align-items-center justify-content-between gap-3">
                        <span>{t(p.labelKey)}</span>
                        <PaletteSwatches paletteId={p.id} swatches={p.swatches.slice(0, 4)} />
                    </span>
                </NavDropdown.Item>
            ))}

            <NavDropdown.Divider />
            <NavDropdown.Header>{t('appearance.modeLabel')}</NavDropdown.Header>
            {MODES.map((m) => (
                <NavDropdown.Item
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    active={mode === m.id}
                    data-testid={`theme-mode-${m.id}`}
                >
                    <i className={`bi ${m.icon} me-2`} />
                    {t(m.labelKey)}
                </NavDropdown.Item>
            ))}

            <NavDropdown.Divider />
            <NavDropdown.ItemText className="small text-muted" style={{ maxWidth: 260 }}>
                {t('appearance.temporaryNotice')}
            </NavDropdown.ItemText>
        </NavDropdown>
    );
};

export default ThemeSwitcher;
